import type { QueryResultLike } from '@krp/client-runtime'

import { actionView, viewOf } from '../../../shared/views'
import type { MutationResultLike } from '../../../shared/views'
import { meanOf, RUN_STATES, shareOf, whenText } from './selectors'
import type {
  TwinFamiliesVm,
  TwinFamily,
  TwinFamilies,
  TwinFamilyVm,
  TwinPriority,
  TwinRun,
  TwinRunDetail,
  TwinRunDetailVm,
  TwinRunRowVm,
  TwinRunsVm,
  TwinVariant,
  TwinVariantVm,
} from './twinTypes'
import type { MutationStateVm, StateBadgeVm } from './types'

/**
 * vm экрана «Похожие варианты» (RP-FB67): числа сервера словами. Каждая доля — с числом кадров и интервалом: «0 %» на трёх кадрах не значит «не путает».
 * Формулы здесь не пересчитываются — их считает сервер.
 */

export const TWIN_PRIORITY_BADGES: Readonly<Record<TwinPriority, StateBadgeVm>> = {
  partial: { tone: 'busy', text: 'Фото есть не у всех — нужны фото' },
  full: { tone: 'ok', text: 'Фото есть у всех вариантов' },
  none: { tone: 'muted', text: 'Фото нет ни у одного варианта' },
}

export const TWIN_PRIORITY_TITLES: Readonly<Record<TwinPriority, string>> = {
  partial: 'Нужны фото',
  full: 'Фото у всех',
  none: 'Без фото',
}

const SCOPE_TITLES: Readonly<Record<string, string>> = { twins: 'кадры двойников', 'all-frames': 'все кадры' }

const PREP_TITLES: Readonly<Record<string, string>> = {
  none: 'без обрезки кадра',
  'background-removed': 'с удалением фона',
  'background-removed-cropped': 'с удалением фона и обрезкой',
  'background-removed-prepared': 'с удалением фона (u2net), обрезкой и кадром без потерь',
  'background-removed-vision': 'с удалением фона (Apple Vision), обрезкой и кадром без потерь (прежний режим)',
  'product-box': 'по рамке товара с запасом вместе с фоном, кадр без потерь (прежний режим)',
  'product-box-tight': 'по плотной рамке товара вместе с фоном, одно уменьшение до 384, кадр без потерь',
}

const NO_DATA = 'нет данных'
const OWN_RUNNING = 'Замер уже идёт — дождитесь его или отмените'
const NO_DONE_RUN = 'Готового замера ещё нет — запустите замер'

const TWIN_DELTA_TITLES: Readonly<Record<string, string>> = {
  confused: 'Перепутали вариант',
  topIsTwin: 'Двойник первым в списке',
  choiceOffered: 'Предложен выбор варианта',
  foundVerdict: 'Ответ «найден»',
  foundCorrect: 'Нашли верно',
  photoFixedText: 'Фото исправило текст',
  photoSpoiledText: 'Фото испортило текст',
  mrr: 'Обратное место (MRR)',
  latencyMedianMs: 'Время ответа, медиана',
}

const fraction = new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const signedFraction = new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2, signDisplay: 'exceptZero' })

function isShare(metric: string): boolean {
  return metric !== 'mrr' && metric !== 'latencyMedianMs'
}

function valueText(metric: string, value: number | null): string {
  if (value === null) return NO_DATA
  if (metric === 'latencyMedianMs') return `${Math.round(value)} мс`
  return isShare(metric) ? `${Math.round(value * 100)} %` : fraction.format(value)
}

function deltaText(metric: string, value: number | null): string {
  if (value === null) return NO_DATA
  if (metric === 'latencyMedianMs') return `${value > 0 ? '+' : ''}${Math.round(value)} мс`
  return isShare(metric) ? `${value > 0 ? '+' : ''}${Math.round(value * 100)} п.` : signedFraction.format(value)
}

export const isTwinRunActive = (run: TwinRun): boolean => run.state === 'queued' || run.state === 'running'

// --- семейства ----------------------------------------------------------------------------------------------------------

function variantOf(variant: TwinVariant, family: TwinFamily): TwinVariantVm {
  return {
    catalogProductId: variant.catalogProductId,
    name: variant.name,
    differences: variant.differences,
    photo: variant.hasPhoto
      ? { tone: 'ok', text: 'Фото есть' }
      : { tone: 'bad', text: 'Фото нет — по фото этот вариант не узнать' },
    framesText: variant.frames === 0 ? 'Кадров нет' : `Кадров: ${variant.frames}`,
    series: family.key,
  }
}

function familyOf(family: TwinFamily): TwinFamilyVm {
  return {
    key: family.key,
    series: family.series,
    priority: TWIN_PRIORITY_BADGES[family.priority],
    summary: `Вариантов ${family.variants.length}, с фото ${family.withPhoto}`,
    variants: family.variants.map((variant) => variantOf(variant, family)),
    missingFrames: family.variants.filter((variant) => variant.frames === 0).length,
  }
}

export interface TwinFamiliesVmInput {
  families: QueryResultLike<TwinFamilies>
  priority: TwinPriority | null
  command: MutationResultLike<unknown> | null
}

export function buildTwinFamiliesVm(input: TwinFamiliesVmInput): TwinFamiliesVm {
  const data = input.families.data ?? null
  const shown = data?.families ?? []
  return {
    query: viewOf(input.families, (page) => page.families.length === 0),
    families: shown.map(familyOf),
    note:
      data === null
        ? null
        : data.total > shown.length
          ? `Показано ${shown.length} из ${data.total} семейств. Остальные — после того, как эти получат кадры, или по отбору выше.`
          : null,
    builtAtText: data === null ? null : `Посчитано ${whenText(data.builtAt)} по ${data.catalogProducts} товарам каталога`,
    priority: input.priority,
    action: actionStateOf(input.command),
  }
}

function actionStateOf(result: MutationResultLike<unknown> | null): MutationStateVm {
  return actionView(result)
}

// --- замеры -------------------------------------------------------------------------------------------------------------

function progressOf(run: TwinRun): string {
  return `${run.doneFrames} из ${run.totalFrames} кадров`
}

function rowOf(run: TwinRun): TwinRunRowVm {
  return {
    id: run.id,
    state: RUN_STATES[run.state],
    when: whenText(run.createdAt),
    scopeText: SCOPE_TITLES[run.scope] ?? run.scope,
    progress: progressOf(run),
    confused: run.metrics === null ? null : shareOf(run.metrics.confused),
    cancellable: isTwinRunActive(run),
  }
}

export interface TwinRunsVmInput {
  runs: QueryResultLike<readonly TwinRun[]>
  /** Выбранный замер: его разбор (`null` — разбирать нечего, например замеров ещё нет). */
  detail: QueryResultLike<TwinRunDetail> | null
  /** Идёт ли замер другого вида: очередь замеров общая, сервер второй не примет. */
  otherActive: boolean
  command: MutationResultLike<unknown> | null
}

export function buildTwinRunsVm(input: TwinRunsVmInput): TwinRunsVm {
  const runs = input.runs.data ?? []
  const detail = input.detail?.data ?? null
  return {
    query: viewOf(input.runs, (rows) => rows.length === 0),
    rows: runs.map(rowOf),
    detail: detail === null ? null : detailOf(detail),
    running: runs.some(isTwinRunActive) ? OWN_RUNNING : input.otherActive ? 'Идёт замер другого вида — очередь замеров общая, дождитесь его' : null,
    exportBlocked: runs.some((run) => run.state === 'done') ? null : NO_DONE_RUN,
    action: actionStateOf(input.command),
  }
}

function detailOf(detail: TwinRunDetail): TwinRunDetailVm {
  const { run } = detail
  const metrics = run.metrics
  const stamp = run.stamp
  return {
    id: run.id,
    title: `Замер ${whenText(run.createdAt)} · ${SCOPE_TITLES[run.scope] ?? run.scope}`,
    state: RUN_STATES[run.state],
    progress: progressOf(run),
    error: run.error,
    scores:
      metrics === null
        ? []
        : [
            {
              key: 'confused',
              label: 'Перепутали вариант',
              share: shareOf(metrics.confused),
              hint: 'Поиск уверенно назвал двойника вместо нужного товара. Главная метрика, цель — 0.',
            },
            { key: 'topIsTwin', label: 'Двойник первым в списке', share: shareOf(metrics.topIsTwin), hint: 'Первым в выдаче стоит двойник, а не нужный товар.' },
            {
              key: 'choiceOffered',
              label: 'Предложен выбор варианта',
              share: shareOf(metrics.choiceOffered),
              hint: 'Поиск не угадывал, а показал оба варианта на выбор.',
            },
            { key: 'foundCorrect', label: 'Нашли верно', share: shareOf(metrics.foundCorrect), hint: 'Ответ «найден», и нужный товар стоит в «Это он?».' },
            { key: 'foundVerdict', label: 'Ответ «найден»', share: shareOf(metrics.foundVerdict), hint: 'Любой ответ «найден», верный или нет.' },
            {
              key: 'photoFixedText',
              label: 'Фото исправило текст',
              share: shareOf(metrics.photoFixedText),
              hint: 'Среди кадров, где текст один не назвал ответ (нет числа или ничья): фото вывело верный товар первым.',
            },
            {
              key: 'photoSpoiledText',
              label: 'Фото испортило текст',
              share: shareOf(metrics.photoSpoiledText),
              hint: 'Среди кадров, где текст один был верен: фото увело на другой товар.',
            },
            { key: 'mrr', label: 'Обратное место (MRR)', share: meanOf(metrics.mrr), hint: 'Чем ближе к 1, тем выше стоит нужный товар.' },
          ],
    series: (metrics?.series ?? []).map((slice) => ({
      series: slice.series === '' ? 'без серии' : slice.series,
      frames: slice.frames,
      confused: shareOf(slice.confused),
      choiceOffered: shareOf(slice.choiceOffered),
      foundCorrect: shareOf(slice.foundCorrect),
    })),
    confusions: (metrics?.confusions ?? []).map((confusion, index) => ({
      key: `${index}`,
      text: `«${confusion.expectedName === '' ? 'товар удалён' : confusion.expectedName}» → «${confusion.insteadName === '' ? 'товар удалён' : confusion.insteadName}»`,
      count: confusion.count,
    })),
    frames:
      metrics === null
        ? ''
        : `Кадров двойников ${metrics.twinFrames}, прочих ${metrics.otherFrames}${metrics.errors === 0 ? '' : `, со сбоем ${metrics.errors} (в баллы не входят)`}`,
    latency:
      metrics === null || metrics.latency.medianMs === null
        ? null
        : `медиана ${Math.round(metrics.latency.medianMs)} мс, 95-й процентиль ${Math.round(metrics.latency.p95Ms ?? metrics.latency.medianMs)} мс, n = ${metrics.latency.n}`,
    stamp:
      stamp === null
        ? []
        : [
            { label: 'Правила вердикта', value: stamp.verdict },
            { label: 'Формула уверенности', value: stamp.confidenceFormula },
            { label: 'Фото кадров', value: PREP_TITLES[stamp.photoPrep] ?? stamp.photoPrep },
            { label: 'Версия Platform', value: stamp.platformVersion },
            { label: 'Модель признаков', value: stamp.encoderModel ?? 'не известна' },
            { label: 'Состав', value: `${SCOPE_TITLES[stamp.scope] ?? stamp.scope}: кадров ${stamp.frames}, серий ${stamp.families}, вариантов ${stamp.variants}` },
            {
              label: 'Эталоны у вариантов',
              value: stamp.twinPhotoCoverage === null ? NO_DATA : `у ${Math.round(stamp.twinPhotoCoverage * 100)} % вариантов есть эталонное фото`,
            },
          ],
    comparison:
      detail.comparison === null
        ? null
        : {
            previousRunId: detail.comparison.previousRunId,
            note: detail.comparison.sameStamp
              ? null
              : 'Прошлый замер посчитан другими правилами вердикта, формулой уверенности, режимом фото или моделью — разница не только в поиске',
            rows: detail.comparison.deltas.map((delta) => ({
              label: TWIN_DELTA_TITLES[delta.metric] ?? delta.metric,
              previous: valueText(delta.metric, delta.previous),
              current: valueText(delta.metric, delta.current),
              delta: deltaText(delta.metric, delta.delta),
            })),
          },
  }
}

/** Какой замер разбирать по умолчанию: выбранный человеком, иначе последний готовый, иначе самый свежий. */
export function defaultTwinRunId(runs: readonly TwinRun[], selected: string | null): string | null {
  if (selected !== null && runs.some((run) => run.id === selected)) return selected
  return (runs.find((run) => run.state === 'done') ?? runs[0])?.id ?? null
}
